import React, { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import LabelInput from '../components/LabelInput';
import { useAuth } from '../context/Auth.context';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Error from '../components/Error';
import { useLoginContext } from '../context/LoginContext';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  FormControl,
  Heading,
  Link,
  VStack,
  Flex,
  Stack,
  Text,
  useBreakpointValue
} from '@chakra-ui/react';

const validationRules = {
  email: {
    name: 'email',
    required: 'Email is required',
  },
  password: {
    required: 'Password is required',
  },
};

export default function Login() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [customError, setCustomError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const methods = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { handleSubmit } = methods;
  const { showLoginMessage } = useLoginContext();

  const handleCancel = () => {
    navigate('/');
  };

  const handleLogin = useCallback(
    async (formData, event) => {
      // Voorkom het standaard formulier-submit gedrag
      event.preventDefault();
      
      const { email, password } = formData;
      
      try {
        setCustomError(null); // Reset any previous error
        
        const loggedIn = await login(email, password);
        
        if (loggedIn) {
          // Store email in localStorage after successful login
          localStorage.setItem('email', email);
          
          // Show success message and navigate
          showLoginMessage('Succesvol ingelogd');
          navigate('/');
        }
      } catch (err) {
        console.error('Login error:', err);
        if (err?.response?.status === 401) {
          setCustomError('Ongeldige e-mail of wachtwoord');
        } else {
          setCustomError('Er ging iets mis. Probeer opnieuw.');
        }
      }
    },
    [login, navigate, showLoginMessage]
  );

  return (
    <FormProvider {...methods}>
      <Flex
        as="form"
        id="loginForm"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(handleLogin)(e);
        }}
        direction="column"
        align="center"
        justify="flex-start"
        minH="100vh"
        px={4}
        pt={{ base: '20vh', md: '25vh', lg: '15vh' }}
      >
        <Box w={['100%', '90%', '70%', '800px']} maxW="800px">
          <Heading mb={6} textAlign="center">
            Log in
          </Heading>

          {customError && (
            <Box
              bg="red.50"
              border="1px solid"
              borderColor="red.200"
              color="red.700"
              p={3}
              borderRadius="md"
              textAlign="center"
              mb={4}
            >
              {customError}
            </Box>
          )}

          <Stack spacing={4}>
            <FormControl>
              <LabelInput
                label="Email"
                name="email"
                placeholder="your@email.com"
                type="text"
                validationRules={validationRules.email}
                data-cy="emailInput"
              />
            </FormControl>

            <FormControl>
              <LabelInput
                label="Wachtwoord"
                name="password"
                placeholder="password"
                type="password"
                validationRules={validationRules.password}
                data-cy="passwordInput"
              />
            </FormControl>

            <Link as={RouterLink} to="/register" color="blue.500" textAlign="center">
              Registreer je hier
            </Link>

            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              data-cy="login"
              w="full"
              ml={0}
            >
              Log in
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              w="full"
              ml={0}
            >
              Annuleer
            </Button>
          </Stack>
        </Box>
      </Flex>
    </FormProvider>
  );
}
